<template>
  <section>
    <div>
      <!-- Heading -->
      <coozzy-page-title
        :title="$tc('message.ticketOverview.title')"
        :count="ticketLength"
        :count-text="$tc('message.ticketOverview.title')"
        :action-text="isTicketingEnabled && (isEditor || isTicketEditor || isTicketCreator) ? $tc('message.ticketOverview.createTicket') : ''"
        :clear-filter="checkfilter"
        @click="createTicket"
        @clear-filter="clickOnClear">
        <template #after-title>
          <div class="col-12 col-md-2 mr-auto">
            <coozzy-button
              design="transparent"
              class="mt-2"
              data-cy="ticket-column-view-btn"
              @click="changeColumnView(!isColumnView)">
              <coozzy-list-icon v-if="isColumnView" />
              <coozzy-column-icon v-else />
            </coozzy-button>
          </div>
        </template>
      </coozzy-page-title>
      <ticket-overview-data
        ref="ticket-overview-data"
        :link-email="linkEmail"
        :name-linked="nameLinked"
        :default-text="defaultText"
        :default-type="defaultType"
        :default-creator="defaultCreator"
        :default-assigne="defaultAssigne"
        :default-priority="defaultPriority"
        :default-overdue="defaultOverdue"
        :default-watched="defaultWatched"
        :default-new-event="defaultNewEvent"
        :default-uploaded-invoice="defaultUploadedInvoice"
        :default-operator="defaultOperator"
        :default-empty-assignee="defaultEmptyAssignee"
        :owner-ids="ownerIds"
        :reference-ids="referenceIds"
        @is-filtered="setCheckfilter"
        @ticket-length="getTicketLength" />
    </div>
  </section>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import CoozzyPageTitle from '@/framework/layout/CoozzyPageTitle.vue'
import TicketOverviewData from './TicketOverviewData.vue'
import CoozzyListIcon from '@/framework/components/icons/CoozzyListIcon.vue'
import CoozzyColumnIcon from '@/framework/components/icons/CoozzyColumnIcon.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import { routeChecks } from '@/mixins/routeChecks'
import { user } from '@/mixins/user'

type LocalRefMethods = {
  setColumnView: (value: boolean) => void
  initFilter: () => void
}

export default Vue.extend({
  name: 'TicketOverview',
  components: {
    CoozzyPageTitle,
    TicketOverviewData,
    CoozzyListIcon,
    CoozzyColumnIcon,
    CoozzyButton
  },
  metaInfo() {
    return {
      title: this.$t('message.navigation.tickets')
    }
  },
  mixins: [routeChecks, user],
  beforeRouteLeave(to, from, next) {
    if (to.fullPath !== from.fullPath && to.fullPath !== localStorage.getItem('ticket-path')) {
      if (from.query.linkEmail) {
        // remove linkMail parameter from the full path
        const modifiedPath = from.fullPath.replace(`&linkEmail=${from.query.linkEmail}`, '')
        localStorage.setItem('ticket-path', modifiedPath)
      }
      next()
    } else {
      next()
    }
  },
  props: {
    defaultText: {
      type: String as PropType<string>,
      default: ''
    },
    defaultType: {
      type: String as PropType<string>,
      default: ''
    },
    defaultCreator: {
      type: String as PropType<string>,
      default: ''
    },
    defaultAssigne: {
      type: String as PropType<string>,
      default: ''
    },
    defaultPriority: {
      type: String as PropType<string>,
      default: ''
    },
    defaultOperator: {
      type: String as PropType<string>,
      default: ''
    },
    defaultOverdue: {
      type: String as PropType<string>,
      default: ''
    },
    defaultWatched: {
      type: String as PropType<string>,
      default: ''
    },
    defaultEmptyAssignee: {
      type: [Boolean, String] as PropType<boolean | string>,
      default: false
    },
    linkEmail: {
      type: String as PropType<string | null>,
      default: null
    },
    nameLinked: {
      type: String as PropType<string | null>,
      default: null
    },
    ownerIds: {
      type: String as PropType<string>,
      default: ''
    },
    referenceIds: {
      type: String as PropType<string>,
      default: ''
    },
    defaultNewEvent: {
      type: String as PropType<string>,
      default: ''
    },
    defaultUploadedInvoice: {
      type: String as PropType<string>,
      default: ''
    }
  },
  data() {
    return {
      checkfilter: false,
      ticketLength: 0,
      isColumnView: true
    }
  },
  created() {
    // check if the user has a ticket path in the local storage with the same beginning of the current path we will redirect him to the ticket path and remove the ticket path from the local storage
    const ticketPath = localStorage.getItem('ticket-path')
    if (ticketPath && this.$route.path.startsWith(ticketPath.substring(0, 10)) && this.linkEmail === null) {
      this.$router.replace({ path: ticketPath }, () => {
        localStorage.removeItem('ticket-path')
      })
    }
  },
  mounted() {
    if (localStorage.getItem('is-column-view-ticket')) {
      this.isColumnView = localStorage.getItem('is-column-view-ticket') === 'true'
    }
    const overviewDataRef = this.$refs['ticket-overview-data'] as unknown as LocalRefMethods
    overviewDataRef.setColumnView(this.isColumnView)
  },
  methods: {
    changeColumnView(newVal: boolean) {
      this.isColumnView = newVal
      const overviewDataRef = this.$refs['ticket-overview-data'] as unknown as LocalRefMethods
      overviewDataRef.setColumnView(newVal)
      localStorage.setItem('is-column-view-ticket', String(newVal))
    },
    createTicket() {
      if (this.isOwnerModule) {
        this.$router.push({ name: 'OwnerTicketCreation' }, () => {
        })
      } else if (this.isAdminModule) {
        this.$router.push({ name: 'AdminTicketCreation' }, () => {
        })
      } else if (this.isAssetModule) {
        this.$router.push({ name: 'AssetTicketCreation' }, () => {
        })
      } else if (this.isAccountingModule) {
        this.$router.push({ name: 'AccountingTicketCreation' }, () => {
        })
      } else {
        this.$router.push({ name: 'MarketingTicketCreation' }, () => {
        })
      }
    },
    getTicketLength(value: number) {
      this.ticketLength = value
    },
    setCheckfilter(value: boolean) {
      this.checkfilter = value
    },
    clickOnClear() {
      const overviewDataRef = this.$refs['ticket-overview-data'] as unknown as LocalRefMethods
      overviewDataRef.initFilter()
    }
  }
})
</script>
